import axios from 'axios';
import { encryptData } from '../utils/helpers';
import Vue from 'vue';
import jwt from 'jsonwebtoken';

import { formatDataEvents } from './serializer';

import graph from './graph';

const {
  VUE_APP_API_URL,
  VUE_APP_API_CHECKOUT,
  VUE_APP_BASE_URL_JN,
  VUE_APP_API_PLATFORM,
} = process.env;

const baseUrlJn = VUE_APP_BASE_URL_JN;
const checkoutUrl = VUE_APP_API_CHECKOUT;

const userUrl = `${VUE_APP_API_PLATFORM}/endpoints`;
const verification = `${VUE_APP_API_PLATFORM}/profile`;
const sunatUrl = `${VUE_APP_API_PLATFORM}/configs`;
const profileUrl = `${VUE_APP_API_PLATFORM}/profile`;
const activityUrl = `${VUE_APP_API_PLATFORM}/activity`;
const auth = `${VUE_APP_API_PLATFORM}/auth`;
let cancelTokenSource;
axios.defaults.baseURL = VUE_APP_API_URL;

const graphqlAxios = axios.create({
  baseURL: `${VUE_APP_API_PLATFORM}/graphql/graphql`,
  timeout: 30000,
});
const axiosAuth = axios.create({
  baseURL: `${VUE_APP_API_PLATFORM}/account`,
  timeout: 30000,
  headers: null,
});
const ticketing = axios.create({
  baseURL: `${VUE_APP_API_PLATFORM}/ticketing/v1`,
  timeout: 30000,
  headers: null,
});
const cashless = axios.create({
  baseURL: `${VUE_APP_API_PLATFORM}/cashless/v1`,
  timeout: 30000,
  headers: null,
});

const signToken = (payload) => jwt.sign(payload, 'jnputin');

class Api {
  constructor() {
    this.axios = axios;
    this.graphqlAxios = graphqlAxios;
    this.token = null;
    this.userAuth = null;
    this.checkoutUrl = checkoutUrl;
    this.userUrl = userUrl;
    this.ticketing = ticketing;
    this.verificationUrl = verification;
    this.sunatUrl = sunatUrl;
    this.axiosAuth = axiosAuth;
    this.baseUrlJn = baseUrlJn;
    this.cashless = cashless;
    this.profileUrl = axios.create({
      baseURL: `${profileUrl}/v1`,
      timeout: 30000,
    });
    this.auth = axios.create({
      baseURL: `${auth}/v1`,
      timeout: 30000,
    });
    this.activityUrl = axios.create({
      baseURL: `${activityUrl}/v1`,
      timeout: 30000,
    });
  }

  async login(payload, headers) {
    const { data } = await this.axios.post('/auth/login', payload, { headers });
    return data;
  }

  async register(payload) {
    const { data } = await this.axios.post('/auth/signup', payload);
    return data;
  }

  async getEvents(q = '', excludeFormat = false) {
    try {
      const resp = await this.axios.get(`/marca-blanca/events?session=${this.token}${q}&visibility=all&origin=pos`);
      if (excludeFormat) {
        return resp.data && resp.data.length ? resp.data[0] : [];
      }
      if (resp.data) return resp.data.length ? formatDataEvents(resp.data) : [];
      return [];
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getDataEvent(eventId) {
    try {
      const { data } = await this.axios.get(`/PE/events/${eventId}`);
      if (!data.status) return false;
      return {
        eventData: data.data,
      };
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getSeatsStringByBlockId(payload) {
    try {
      const {
        data: { data },
      } = await this.axios.post('/theater/event-seats-block', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getSeatsBuyed(payload) {
    try {
      const {
        data: { data },
      } = await this.axios.post('/theater/event-seats-busy', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async sendTickets(payload, token) {
    delete this.ticketing.defaults.headers.common['Authorization'];
    try {
      const { data } = await this.ticketing.post( '/joinnus/ticketing/ticket/send', payload,
        {
          headers: {
            cookie_joinnus: token,
          },
        }
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async saveDataPost(payload, token) {
    try {
      const { data } = await this.profileUrl.post( '/boxoffice/cashout', payload,
        {
          headers: {
            cookie_joinnus: token,
          },
        }
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async closeLote(payload, token) {
    try {
      const { data } = await this.activityUrl.post( '/boxoffice/close-lote', payload,
        {
          headers: {
            cookie_joinnus: token,
          },
        }
      );
      return data;
    } catch (e) {
      const { message } = e.response.data
      return {
        status: false,
        message
      }
    }
  }

  async getSeatsBlock(payload) {
    const {
      data: { data },
    } = await this.axios.post('/theater/event-seats-blocked', payload);
    return data;
  }

  async seatVerifyAndReserved(payload) {
    try {
      const {
        data: { data },
      } = await this.axios.post('/theater/event-seat-valid', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  // Products
  async getProducts({ q = '', eventId = null }) {
    try {
      const query = graph.queryProducts({ session: this.token, q, eventId });
      const {
        data: {
          data: {
            merchandises: { data },
          },
        },
      } = await this.graphqlAxios({
        method: 'post',
        data: { query },
      });
      return data.map((el) => {
        Vue.set(el, 'quantity', el.stock >= 1 ? 1 : 0);
        Vue.set(el, 'variationId', null);
        return el;
      });
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async reservateAndPayProducts(products, user, payment, eventId, coupons, cash, cashReturn, transaction, delivery) {
    let errors = [];
    let methods = [];
    const { document, documentType, email } = user;
    // reservate
    const payloadRes = {
      products,
      tickets: [],
      points: [],
      user: {
        id: null,
        email: email || null,
        documentType: document ? documentType : null,
        document,
        session: null,
      },
      sessionId: this.token,
      userCode: null,
      eventId,
      coupons,
      transaction,
      delivery,
    };
    const tokenRes = signToken(payloadRes);
    const { data } = await this.axios.post(`${checkoutUrl}/reservate`, { details: tokenRes });
    if (!data.success) {
      errors = typeof data.message === 'string' ? [data.message] : data.message;
      return {
        success: false,
        errors,
      };
    }

    //Payment Delivery
    if (delivery.isDeliveryProccess) {
      const payload = {
        transactionCode: data.transactionCode,
        deliveryTypeId: 2,
        organizerId: '',
        scheduledDate: delivery.dateTime.value,
        addressId: delivery.address.id,
        zoneMethodId: delivery.zoneMethodId,
      };
      const details = { details: signToken(payload) };
      try {
        await this.axios.post(`${checkoutUrl}/payment/delivery`, details);
      } catch (e) {
        return await this.captureAndLogMessage(e.response);
      }
    }

    // register pay
    transaction.code = data.transactionCode;
    if (payment.type === 'multiple') {
      methods = [
        {
          method: 'pos',
          cardType: payment.mixed.card,
          amount: payment.mixed.amount,
          paymentTransactionId: payment.mixed.code,
          amountPayed: payment.mixed.amount,
        },
        {
          method: 'efectivo',
          amount: payment.mixed.amountCash,
          amountPayed: cash,
          amountReturned: cashReturn,
        },
      ];
    } else {
      methods = [
        {
          method: payment.type,
          cardType: payment.card,
          amount: payment.amount,
          paymentTransactionId: payment.code,
          amountPayed: payment.type !== 'efectivo' ? payment.amount : cash,
          amountReturned: cashReturn,
        },
      ];
    }
    const payloadPay = {
      user: {
        email: null,
        documentType: document ? documentType : null,
        document,
        codeCountry: 'PE',
        id: null,
        session: null,
      },
      transaction,
      eventId,
      method: payment.type,
      sessionId: this.token,
      methods,
    };
    const tokenPay = signToken(payloadPay);
    const { data: resPay } = await this.axios.post(`${checkoutUrl}/payment`, { details: tokenPay });

    if (!resPay.success) {
      if (resPay.message.length > 1) {
        errors = resPay.message;
      } else {
        errors.push(resPay.message);
      }
      return {
        success: false,
        errors,
      };
    }
    return {
      success: true,
      data: resPay,
    };
  }

  async getStock(id) {
    try {
      const { data } = await this.axios.get(`/products/${id}?session=${this.token}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getCouponProducts(activityId) {
    try {
      const payload = {
        session: this.token,
        activityId,
      };
      const {
        data: { data },
      } = await this.axios.post('/coupons/seller', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getReportTransaction(payload) {
    try {
      payload.session = this.token;
      const { data } = await this.axios.post('/merchandise/transactions', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getReportUser(token, params) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get('/ticketing/resume-of-seller', {
        headers: {
          cookie_joinnus: token,
        },
        params,
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async annulTransaction(token, ticketId, reason) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.post(
        '/ticketing/transaction-annul',
        { ticketId, reason },
        {
          headers: {
            cookie_joinnus: token,
          },
        },
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getBuyerResumeByActivityId(token, payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get('/ticketing/buyer-resume', {
        headers: {
          cookie_joinnus: token,
        },
        params: payload,
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  downloadReport(token) {
    this.axios({
      url: `ticket/resume-of-seller/export?session=${token}`,
      method: 'GET',
      responseType: 'blob',
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users.xlsx`);
      document.body.appendChild(link);
      link.click();
    });
  }
  async exportData(token) {
    const response = await this.axios.get(`ticket/resume-of-seller/export?session=${token}`);
    if (response) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users.xlsx`);
      document.body.appendChild(link);
      link.click();
    }
  }
  async getReportPaymentMethods(payload) {
    try {
      payload.session = this.token;
      const { data } = await this.axios.post('/merchandise/report/payment-methods', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getListUsers(activityId) {
    try {
      const payload = {
        session: this.token,
        activityId,
      };
      const {
        data: { data },
      } = await this.axios.post('/merchandise/report/users', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async cancelTransaction(payload) {
    try {
      payload.sessionId = this.token;
      const token = signToken(payload);
      const { data } = await this.axios.post(`${checkoutUrl}/transaction/cancel`, { details: token });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getElectronicBilling(payload) {
    try {
      const { data } = await this.axios.get('/electronic-billing/voucher', { params: payload });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async sendEmail(payload) {
    try {
      const {
        data: { data },
      } = await this.axios.post('/electronic-billing/send-email', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getCreditNote(payload) {
    try {
      const { data } = await this.axios.get('/electronic-billing/credit-note', { params: payload });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getReportTransactionPoints(payload) {
    try {
      const { data } = await this.axios.get('/points/report/sales', { params: payload, 
        headers: {
          cookie_joinnus: this.token,
        }, });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  // reservation for unilabs
  async reservation(token, sesion, source, search = '', product) {
    try {
      const res = await axios({
        url: `${this.checkoutUrl}/reservation`,
        method: 'post',
        data: { details: token },
      });
      let route = '';
      if (res.data.data) {
        const ses = `?session=${res.data.sessionId}`;
        const prod = product ? `&product=${product.id}` : '';
        const src = `&source=${source}`;
        route = `${baseUrlJn}/checkout-payment/${res.data.data}${ses}${src}${prod}${search}`;
        return { route, status: true, data: res.data };
      }
      return { message: res.data.message, status: false };
    } catch (error) {

      return { message: 'Inténtalo en un momento.', status: false };
    }
  }

  async sendLinkEmail(payload) {
    try {
      const { data } = await this.axios.post('/email-sender', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getEventDates(id) {
    try {
      const {
        data: { multidates },
      } = await this.axios.get(`/PE/events/${id}/dates`);
      return multidates;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getEventTypes(id, hourId, filter = '') {
    try {
      const {
        data: { data },
      } = await this.axios.get(`/PE/events/${id}/types?dateHour=${hourId}${filter}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getAllTickets(id) {
    try {
      const { data } = await this.axios.get(`/PE/events/${id}/tickets-dates`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getVerifyTickets(id, args) {
    try {
      const pStr = JSON.stringify(args.payload);
      const allHours = args.allHours ? '&allHours=true' : '';
      const { data } = await this.axios.get(`/PE/events/${id}/types-stock?tickets=${pStr}${allHours}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async verifyCouponByEvent(eventId, payload) {
    try {
      const { data } = await this.axios.post(`/PE/events/${eventId}/discount-code?origin=offline`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //Deliveries
  async getDeliveriesDates(payload) {
    try {
      const { data } = await this.axios.post('/merchandise/delivery-date', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getDeliveryZones({ storeId }) {
    try {
      const query = graph.deliveryZones({ storeId });
      const { data } = await this.graphqlAxios({
        method: 'post',
        data: { query },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async editUserAddress(idConsumer, idAddress, payload) {
    try {
      const { data } = await this.axios.put(`/addresses/${idAddress}?userId=${idConsumer}`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async newUserAddress(idConsumer, payload) {
    try {
      const { data } = await this.axios.post(`/addresses?userId=${idConsumer}`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getUserAddresses(idConsumer) {
    try {
      const { data } = await this.axios.get(`/addresses?userId=${idConsumer}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //Validate User
  async getUserValid(email) {
    try {
      const { data } = await this.axios.get(`/user/valid?email=${email}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async createUser(payload) {
    try {
      const { data } = await this.userUrl.post('/user', payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //Consul fee payment
  async getGenerateFeePayment(payload) {
    try {
      const { data } = await axios.get(
        `${this.checkoutUrl}/consult-fee-payment?amount=${payload.amount}&activityId=${payload.activityId}`,
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getFeePayment(ticketId) {
    try {
      const { data } = await axios.get(`${this.checkoutUrl}/fee-payment?ticketId=${ticketId}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getTransactionByParams(activityId, payload) {
    try {
      const { data } = await axios.get(
        `${this.userUrl}/activity/${activityId}/search/participants?${payload.type}=${payload.value}`,
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //verication
  async verificationUser(payload) {
    try {
      const { data } = await axios.post(`${this.verificationUrl}/v1/joinnus/auth/verification`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //billing
  async getBillings(userId) {
    try {
      const { data } = await axios.get(`${this.verificationUrl}/v1/joinnus/profile/billing?userId=${userId}`);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async createBilling(payload) {
    try {
      const { data } = await axios.post(`${this.verificationUrl}/v1/joinnus/profile/billing`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async updateBilling(id, payload) {
    try {
      const { data } = await axios.put(`${this.verificationUrl}/v1/joinnus/profile/billing/${id}`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //SUNAT
  async verificationSunat(payload) {
    try {
      const { data } = await axios.post(`${this.sunatUrl}/v1/joinnus/general/search-sunat`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  //INTIMO
  async verificationUserIntimo(payload) {
    try {
      const { data } = await axios.post(`${this.verificationUrl}/v1/clubalianzalima/auth/verification`, payload);
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  // Machu Picchu
  async getListEvents(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(
        '/joinnus/ticketing/event/sales-rates/' + payload.activityId + '?type=' + payload.type,
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getSchedulesReservation(activityId, typeGroupId, payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/ticketing/event/${activityId}/schedule/circuits/${typeGroupId}`, {
        params: payload,
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getListCamps(activityId, activityDateHourId, ticketTypeGroupId, relationId) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/ticketing/event/${activityId}/schedule/camps`, {
        params: {
          ticketTypeGroupId,
          activityDateHourId,
          relationId,
        },
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getListTypesTickets(activityId, dateHourId, typeGroupId) {
    try {
      const type = 'ticket';
      const { data } = await this.ticketing.get(`/joinnus/ticketing/event/tickets/${activityId}`, {
        params: {
          dateHourId,
          type,
          typeGroupId,
        },
      });
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async getSearchParticipantGuidesOrSupportStaff(payload) {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Operation canceled due to new request.');
    }
    cancelTokenSource = axios.CancelToken.source();
    try {
      //TODO: endpoint not require cookie_joinnus
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/joinnus/ticketing/participants/search`, {
        params: payload,
        cancelToken: cancelTokenSource.token,
      });
      if (data.status) {
        data.data.sort(function (a, b) {
          if (a.disabled < b.disabled) return -1;
          else if (a.disabled > b.disabled) return 1;
          else return 0;
        });
        return data.data;
      } else return [];
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled', error.message);
      } else {
        return await this.captureAndLogMessage(error.response);
      }
    }
  }
  async getTicketsList(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const {
        activityId,
        typeGroupId,
        groupType,
        page,
        perPage,
        activityTicketId,
        payed,
        bookingDateStart,
        bookingDateEnd,
        entryDateStart,
        entryDateEnd,
        statusAttach,
        hierarchy,
        categoryId,
      } = payload;
      let customParams = {
        activityId,
        typeGroupId,
        page,
        perPage,
        payed,
      };

      if (groupType) {
        customParams.groupType = groupType;
      }
      if (categoryId) {
        customParams.categoryId = categoryId;
      }
      if (activityTicketId) {
        customParams.activityTicketId = activityTicketId;
      }
      if (hierarchy) {
        customParams.hierarchy = hierarchy;
      }
      if (bookingDateStart) {
        customParams.bookingDateStart = bookingDateStart;
      }
      if (bookingDateEnd) {
        customParams.bookingDateEnd = bookingDateEnd;
      }
      if (entryDateStart) {
        customParams.entryDateStart = entryDateStart;
      }
      if (entryDateEnd) {
        customParams.entryDateEnd = entryDateEnd;
      }
      if (statusAttach) {
        customParams.statusAttach = statusAttach;
      }
      const { data } = await this.ticketing.get('/ticketing/buyer-resume', {
        headers: {
          cookie_joinnus: this.token,
        },
        params: customParams,
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getCountries(payload) {
    try {
      let { typeGroupId, activityId } = payload;
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(
        `/joinnus/ticketing/countries?activityId=${activityId}&ticketGroupId=${typeGroupId}`,
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async setCreateReservationSingle(payload) {
    try {
      const { name, lastname, email, document, documentType, telephone, sexo, id } = this.userAuth;
      const user = {
        name,
        lastname,
        phone: telephone || '000000000',
        email,
        document: document || '00000000',
        documentType: documentType || 'DNI',
        nacionality: 'PE',
        sexo: sexo || 'M',
        sessionId: this.token,
        deviceSessionID: null,
        origin: 'white-label-pos',
        source: 'web',
        iso: 'PE',
        date_birthday: '',
        provincia: '',
      };
      //TODO: payloadDefault.parentTicketId send first time null and edit send activityId
      const payloadDefault = {
        sellingPointId: 8,
        coupon: '',
        participants: payload.participants ? payload.participants : [],
        feePayments: [],
        source: 'box-office',
        buycorporate: 0,
        dataBilling: null,
        reservationOnly: payload.reservationOnly || false,
        ticketId: '',
        createTicket: true,
        sellingUser: id,
        parentTicketId: payload.parentTicketId || null,
        payment: payload.payment
          ? payload.payment
          : {
              ip: null,
              method_payment: 'efectivo',
              currency: 'PEN',
              specialPrice: false,
              reason: '',
              cardType: '',
            },
      };
      payload.user = user;
      const dataConvertJWT = await signToken({ ...payload, ...payloadDefault });
      const { data } = await axios.post(`${this.checkoutUrl}/payment/process/point-of-sale`, { data: dataConvertJWT });
      return data;
    } catch (error) {

      return { message: 'Inténtalo en un momento.', status: false };
    }
  }
  async setPaymentCreatedTicket(payload) {
    try {
      const { name, lastname, email, document, documentType, telephone, sexo, id } = this.userAuth;
      const user = {
        name,
        lastname,
        phone: telephone || '000000000',
        email,
        document: document || '00000000',
        documentType: documentType || 'DNI',
        nacionality: 'PE',
        sexo: sexo || 'M',
        sessionId: this.token,
        deviceSessionID: null,
        origin: 'white-label-pos',
        source: 'web',
        iso: 'PE',
        date_birthday: '',
        provincia: '',
      };
      const payloadDefault = {
        sellingPointId: 8,
        coupon: '',
        participants: payload.participants ? payload.participants : [],
        feePayments: [],
        source: 'box-office',
        buycorporate: 0,
        dataBilling: null,
        reservationOnly: false,
        ticketId: payload.ticketId ? payload.ticketId : '',
        createTicket: false,
        sellingUser: id,
        parentTicketId: payload.parentTicketId || null,
        payment: payload.payment
          ? payload.payment
          : {
              ip: null,
              method_payment: 'efectivo',
              currency: 'PEN',
              specialPrice: false,
              reason: '',
              cardType: '',
            },
      };
      payload.user = user;
      const dataConvertJWT = await signToken({ ...payload, ...payloadDefault });
      const { data } = await axios.post(`${this.checkoutUrl}/payment/process/point-of-sale`, { data: dataConvertJWT });
      return data;
    } catch (error) {

      return { message: 'Inténtalo en un momento.', status: false };
    }
  }
  async addToReservationMoreVisitorOrGuidesAndStaff(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.put('/joinnus/ticketing/reservation/items', payload, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async setSaveParticipantInReservationSingle(payload) {
    const { id } = this.userAuth;
    try {
      payload.user = {
        iso: 'PE',
        session: this.token,
        user_id: id,
        deviceSessionID: null,
      };
      const dataConvertJWT = await signToken(payload);
      const { data } = await axios.post(`${this.checkoutUrl}.1/payment/save-data`, { details: dataConvertJWT });
      return data;
    } catch (error) {

      return { message: 'Error al guardar participante.', status: false };
    }
  }
  async setSaveParticipantGuides(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const dataConvertJWT = await signToken(payload);
      const { data } = await this.ticketing.post(
        `/ticketing/support-staff/save-data`,
        {
          details: dataConvertJWT,
        },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return data;
    } catch (error) {

      return { message: 'Error al guardar el guia.', status: false };
    }
  }
  async getReservationDetail(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get('/joinnus/ticketing/tickets/point-of-sale/' + payload.ticketId, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      if (data.status && data.data && data.data.ticket.source === 'JOINNUS-V3' && !data.data.ticket.sellingPoint) {
        data.data.agency = {
          documentType: 'RUC',
          document: '20537630222',
          businessName: 'MINISTERIO DE CULTURA'
        }
        data.data.ticket.sellingPoint = {
          sellerName: 'WEB PAYMENT',
          sellingPointName: 'JOINNUS ',
          sellingPointAddress: 'WEB'
        }
      }
      if (data.status == true) {
        if (data.data.ticket && !data.data.ticket.symbol) {
          data.data.ticket.symbol = 'S/'
        }
      }
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async setUpdateParticipantInReservation(payload) {
    try {
      payload.session = this.token;
      // TODO: Only one update is allowed for participant
      const { data } = await this.axios.put('/code-exchange/update-participant-v2', payload);
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async setCloseReservation(payload, activityId) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.put(`/ticketing/event/${activityId}/attach`, payload, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async refreshSession() {
    try {
      const payload = {
        session: this.token,
        user_id: this.userAuth.id,
      };
      const { data } = await this.axios.post('/auth/refresh-session', payload);
      if (data.sessionId) {
        this.token = data.sessionId;
        this.userAuth.token = data.sessionId;
        this.userAuth.expire = data.expire;
        this.userAuth.expireTimestamp = data.expireTimestamp;
      }
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }
  async getGenerateCodersQrByTicketId(ticketid) {
    try {
      //TODO: data sample ?ticketid=9103621BA231373032&session=0EwfwL2ksPEetvygRIu2aqcuQKb25w&userid=910362
      const params = {
        session: this.token,
        userid: this.userAuth.id,
        ticketid,
      };
      const { data } = await this.axios.get(`/ticket/codes-qr`, {
        params,
      });
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }

  async getGenerateCodeForMultiPayment(tickets = []) {
    try {
      const { name, lastname, email, document, documentType, id } = this.userAuth;
      const payload = {
        tickets: [],
        ticketId: tickets,
        currency: 'PEN',
        points: [],
        products: [],
        user: {
          id: id || '',
          email: email || '',
          documentType: documentType || 'DNI',
          document: document || '00000000',
          session: this.token,
        },
        sessionId: null,
        userCode: '',
        eventId: localStorage.getItem('activityId'),
        utmSource: 'web',
        coupons: [],
        billing: {
          voucherType: 'TICKET',
          personType: 'PN',
          ruc: '',
          businessName: '',
          address: '',
          document: document || '',
          documentType: documentType || '',
          name: name || '',
          lastName: lastname || '',
          email: email || '',
        },
        origin: '',
        extrafield: [
          {
            key: 'Información adicional',
            value: '',
          },
        ],
      };
      const token = signToken(payload);
      const { data } = await this.axios.post(`${checkoutUrl}/reservate`, { details: token });
      return data;
    } catch (error) {
      return await this.captureAndLogMessage(error.response);
    }
  }

  async getInformationOfMultiTransactions(transactionId) {
    //TODO: transactionId example 48104e7d4a1d8cd89ccc || endpoint no require cookie_joinnus
    const _transactionId = transactionId || '48104e7d4a1d8cd89ccc';
    try {
      const { data } = await this.axios.get(`/transactions/detail/${_transactionId}`);
      data.userAuth = this.userAuth;
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async cancelReservation(ticketId) {
    try {
      const { id } = this.userAuth;
      let payload = {
        ticket: {
          id: ticketId,
        },
        user: {
          iso: 'PE',
          token: null,
          origin: '-',
          session: this.token,
          user_id: id,
        },
      };
      const token = signToken(payload);
      const { data } = await this.axios.put(`${checkoutUrl}/annulled`, { details: token });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async cancelVisitor(item = null) {
    const { ticketId, ticketCodeId, activityId } = item;
    const payload = {
      ticketId,
      ticketCodeId,
    };
    try {
      const resp = await this.ticketing.put(`/ticketing/event/${activityId}/qr-codes/downgrade`, payload, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return resp;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async deleteVisitor(item = null) {
    const { ticketId, ticketCodeId, activityId } = item;
    const payload = {
      activityTicketId: ticketId,
    };
    delete this.ticketing.defaults.headers.common['Authorization'];
    try {
      const resp = await this.ticketing.delete(`/ticketing/event/${activityId}/ticket-codes/${ticketCodeId}`, payload, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return resp;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async forgotPassword(email) {
    try {
      return this.axios.post('/account/forget-password', {
        email: email,
      });
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getUserInformation() {
    try {
      const { data } = await this.axiosAuth.get(`/profile`, {
        headers: {
          Authorization: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async checkEmail(email) {
    try {
      let payload = {
        domain: email,
      };
      const { data } = await this.axiosAuth.post(`/verify-account`, payload, {
        headers: {
          Authorization: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async updateProfile(payload) {
    try {
      const { data } = await this.axiosAuth.put(`/profile`, payload, {
        headers: {
          Authorization: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async changePassword(payload) {
    try {
      const { data } = await this.axiosAuth.post(`/change-password`, payload, {
        headers: {
          Authorization: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getExistVisitorInReservation(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const activityId = localStorage.getItem('activityId');
      const { data } = await this.ticketing.get(`/ticketing/activity/${activityId}/validate-exists-participant`, {
        params: payload,
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getCircuits(payload) {
    try {
      if (!payload.activityTicketId) {
        delete payload.activityTicketId;
      }
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/ticketing/buyer-resume`, {
        headers: {
          cookie_joinnus: this.token,
        },
        params: payload,
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getListReportsReservation(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const {
        page,
        perPage,
        search,
        entryDateStart,
        activityId,
        categoryId,
        hierarchy,
        ticketId,
        ticketGroupId,
        merge,
        state,
      } = payload;
      const { data } = await this.ticketing.get('/ticketing/resume-all-sellers', {
        headers: {
          cookie_joinnus: this.token,
        },
        params: {
          activityId,
          categoryId,
          page,
          perPage,
          search,
          entryDateStart,
          ticketId,
          ticketGroupId,
          state,
          merge,
          hierarchy: hierarchy ? 1 : null,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async setCreateGuidesOrSupportStaffReservationClose(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const dataConvertJWT = await signToken(payload);
      const { data } = await this.ticketing.post(
        '/ticketing/support-staff/save-data',
        { details: dataConvertJWT },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getListCheckSpaceAvailability(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { activityId, typeGroupId, startDate, endDate, categoryId } = payload;
      if (categoryId) {
        const { data } = await this.ticketing.get(`/ticketing/event/${activityId}/schedule/circuits/${typeGroupId}`, {
          params: {
            startDate,
            endDate,
            categoryId,
          },
        });
        return data;
      } else {
        const { data } = await this.ticketing.get(`/ticketing/event/${activityId}/schedule/circuits/${typeGroupId}`, {
          params: {
            startDate,
            endDate,
          },
        });
        return data;
      }
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async exportExcelListReservations(payload) {
    try {
      const { search, activityId, categoryId, hierarchy, ticketGroupId, ticketid,entryDateStart } = payload;
      let url = `${VUE_APP_API_URL}/ticket/resume-all-seller/export?activityId=${activityId}&categoryId=${categoryId}&session=${this.token}`;
      if (search) {
        url += `&search=${search}`;
      }
      if (ticketid) {
        url += `&ticketid=${ticketid}`;
      }
      if (hierarchy) {
        url += `&hierarchy=1`;
      }
      if (ticketGroupId) {
        url += `&ticketGroupId=${ticketGroupId}`;
      }
      if (entryDateStart) {
        url += `&entryDateStart=${entryDateStart}`;
      }
      return url;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getDataDashboard(payload, activityId) {
    delete this.ticketing.defaults.headers.common['Authorization'];
    try {
      const respKpi = await this.ticketing.post(`/ticketing/event/${activityId}/dashboard/kpi`, payload, {
        headers: {
          cookie_joinnus: this.token,
        },
      });
      const respSalesReport = await this.ticketing.post(
        `/ticketing/event/${activityId}/dashboard/sales-report`,
        payload,
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return {
        dataKpi: respKpi.data,
        dataSalesReport: respSalesReport.data,
      };
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async setNewSupport(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.post(
        `/ticketing/event/${payload.activityId}/${payload.staffType}`,
        {
          activityTicketId: payload.activityTicketId,
          ticketGroupId: payload.ticketGroupId,
        },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getPaymentMethods() {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const activityId = localStorage.getItem('activityId');

      const { data } = await this.ticketing.get(`/joinnus/ticketing/activity/${activityId}/payment-methods`, {
        headers: {
          cookie_joinnus: this.token,
        },
      });

      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getCountriesS3() {
    let countries = [];
    let paises = 'https://s3.us-west-2.amazonaws.com/joinnus.com/files/mincu/countriesArray.json';
    let res = await fetch(paises);
    countries = await res.json();
    if (countries.length > 0) {
      return countries;
    } else {
      let paises = '/countries.json';
      let res = await fetch(paises);
      return await res.json();
    }
  }

  async mergerTickets(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.post(
        `/joinnus/ticketing/ticket/merge`,
        {
          mainTicketId: payload.mainTicketId,
          ticketIds: payload.ticketIds,
        },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );

      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async requestMergerTickets(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.post(
        `/joinnus/ticketing/ticket/request/merge`,
        {
          mainTicketId: payload.mainTicketId,
          description: payload.description,
          ticketIds: payload.ticketIds,
        },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );

      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getDataVisitorReducer(data, countries, payload) {
    try {
      let defaultCountry = 1;
      if (payload.documentType === 'DNI') {
        const filterCountry = countries.find((el) => el.iso === 'PE');
        if (filterCountry) {
          defaultCountry = filterCountry.slug;
        }
      }

      let visitor = {
        name: '',
        lastname: '',
        gender: 'M',
        country: defaultCountry,
        brithday: '',
      };

      if (!data) {
        return {
          status: true,
          data: visitor,
        };
      }

      let countryId = 1;
      const countryDefault = data.nacionality || 'PE';
      const filterCountry = countries.find((el) => el.iso === countryDefault);
      if (filterCountry) {
        countryId = filterCountry.slug;
      }

      visitor.name = data.name || '';
      visitor.lastname = data.lastName || '';
      visitor.gender = data.gender || 'M';
      visitor.country = countryId;
      visitor.brithday = data.birthday || '';
      return {
        status: true,
        data: visitor,
      };
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getSearchDataVisitor(payload, countries) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/joinnus/person`, {
        params: payload,
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return await this.getDataVisitorReducer(data.data, countries, payload);
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getMergedList(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/joinnus/ticketing/ticket/request`, {
        params: payload,
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async responseMergeRequest(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.put(
        `/joinnus/ticketing/ticket/request`,
        {
          id: payload.id,
          state: payload.state,
        },
        {
          headers: {
            cookie_joinnus: this.token,
          },
        },
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async getListEventsPublic(activityId,payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(
        '/joinnus/ticketing/event/public-sales-rates/' + activityId,
        {
          params: payload
        }
      );
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  responseData(status,code,message,data){
    return {
      status,
      code,
      message,
      data,
    };
  }
  captureAndLogMessage(response) {
    return new Promise((resolve) => {
      try {
        let { code, message } = response.data.error;
        resolve(this.responseData(false, code, message[0], null));
      } catch (error) {
        let { code } = error.response.data.error;
        resolve(this.responseData(false, code, '', null));
      }
    });
  }
  async searchByInfo(payload) {
    try {
      delete this.ticketing.defaults.headers.common['Authorization'];
      const { data } = await this.ticketing.get(`/ticketing/tickets/search`, {
        params: payload,
        headers: {
          cookie_joinnus: this.token,
        },
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }
  async cashRegister(payload) {
    payload.session = this.token
    try {
      const { data } = await this.axios.get('/ticket/resume/sale-balance/mincu', {
        params: payload,
      });
      return data;
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async validateQr(payload) {
    const headers = {
      'cookie-joinnus': this.token
    }
    try {
      const { data } = await this.cashless.post('/mypurchases/user-codes/validate', payload, { headers });
      return data;
    } catch (e) {
      return e.response.data
    }
  }

  async paymentPoints(payload) {
    const jwtData = signToken(payload);
    try {
      const { data } = await this.axios.post(`${checkoutUrl}/payment/points`, { details: jwtData} );
      return data
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  errorCapture(response) {
    return new Promise((resolve) => {
      try {
        let { message } = response.data;
        console.log({ message });
        console.log(this.errorResponse(false, message, null));
        resolve(this.errorResponse(false, message, null));
      } catch (error) {
        console.log(error);
        resolve(this.errorResponse(false, 'message', '', null));
      }
    });
  }

  errorResponse(status, message, data){
    return {
      status,
      code: 500,
      message,
      data,
    };
  }

  async getRechargePoints(payload, token) {
    const headers = {
      'cookie-joinnus': token
    }
    try {
      const { data } = await this.cashless.get('/reports/recharge-points',
        {
          params: {
            activityId: payload
          },
          headers
        });
      return data;
    } catch (e) {
      return e.response.data
    }
  }

  async sendLoginCode ({ type = 'email', user }) {
    const OPT_TYPE = 'otp-login-two-auth';
    const types = {
      email: 'token-login-two-auth',
      'alt-email': 'token-login-two-auth-email-alternative',
      sms: OPT_TYPE,
      whatsapp: OPT_TYPE,
    };
    const typeKey = types[type];
    const { token } = user;
    const params = {
      type: typeKey,
      token,
    };
    if (['sms', 'whatsapp'].includes(type)) {
      const body = {
        code: user.phoneCode,
        phone: user.phone.toString(),
        option: 'otp',
        type: typeKey,
        token,
      };
      const bodyEncripted = { data: encryptData(body) };
      const { data } = await this.axios.post(`${profileUrl}/v2/joinnus/auth/phone/send/${type}`, bodyEncripted);
      return data;
    }
    const encripted = { data: encryptData(params) };
    try {
      const { data } = await this.axios.post(`${profileUrl}/v1/joinnus/auth/generate/token`, encripted);
      return data;
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);
      throw error;
    }
  }

  async validateLoginCode ({ type = 'email', code, token }) {
    const VERIFY_TYPE = 'verify-login-two-auth';
    const types = {
      email: VERIFY_TYPE,
      'alt-email': 'verify-login-two-auth-email-alternative',
      phone: VERIFY_TYPE,
    };
    const body = {
      otp: code,
      type: types[type],
      token,
    };
    const bodyEncripted = { data: encryptData(body) };
    const { data } = await this.axios.post(`${profileUrl}/v2/joinnus/auth/verify-otp`, bodyEncripted);
    return data;
  }

  async getAccessToken(accessToken) {
    const headers = {
      "Authorization": `Basic ${accessToken}`,
      "Content-Type": "application/json"
    }
    const { data } = await this.auth.post('/authorizer/app', null, { headers });
    return data;
  }

  async getNewListEvents(accessToken, search = '', excludeFormat = false) {
    try {
      this.activityUrl.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      this.activityUrl.defaults.headers.common['cookie_joinnus'] = this.token
      const resp = await this.activityUrl.get(`/boxoffice/selling-user/events?${search}`);
      const items = (resp.data && resp.data.data && resp.data.data.items) ? resp.data.data.items : [];
      if (excludeFormat) {
        return items.length > 0 ? items[0] : [];
      }
      return { items, requiredPosData: resp.data.data.requiredPosData };
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getListSchedules(activityId = 0) {
    try {
      const config = {
        headers:  {
          'cookie_joinnus': this.token
        }
      };
      const resp = await this.activityUrl.get(`/boxoffice/events/${activityId}/schedules`, config);
      return (resp.data && resp.data.data) ? resp.data.data: [];
    } catch (e) {
      return await this.captureAndLogMessage(e.response);
    }
  }

  async getListTicketTypes(activityId = 0, dateHourId = 0) {
    try {
      const config = {
        headers:  {
          'cookie_joinnus': this.token
        }
      };
      const resp = await this.activityUrl.get(`/boxoffice/events/${activityId}/${dateHourId}/ticket-types`, config);
      return (resp.data && resp.data.data) ? resp.data.data: [];
    } catch (e) {
      console.log('Error: getListTicketTypes', e)
      throw e;
    }
  }
}


export default new Api();
